<script lang="ts" setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import ImageResize from "./ImageResize";
import { UploadService } from "@/util";
import { getStorageProxyUrl } from "@/util/StorageProxy";
import { token } from "@/init";
import { v4 as uuid } from "uuid";

Quill.register("modules/imageResize", ImageResize);

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  showSaveButton: {
    type: Boolean,
    default: true,
  },
  editorHeight: {
    type: String,
    default: "400px",
  },
});

const emits = defineEmits(["submit", "changeState"]);

const editor = ref<HTMLElement | null>(null);
const content = ref(props.content);

defineExpose({
  save: () => emits("submit", content.value),
  currentContent: () => content.value,
});

let editorInstance: Quill;

const fileEndings: { [key: string]: string } = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
};

const imageHandler = () => {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");
  input.click();

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    if (!fileEndings.hasOwnProperty(file.type)) {
      alert("Please select a valid image file (PNG, JPG, or GIF)");
      return;
    }

    const maxFileSize = 3 * 1024 * 1024; // 3MB
    if (file.size > maxFileSize) {
      alert("Image file is too large. Maximum size is 3MB.");
      return;
    }

    try {
      const formData = new FormData();
      const id = uuid();
      const fileName = `${id}.${fileEndings[file.type]}`;
      formData.append("file", file, fileName);

      const imageUrl = await UploadService.uploadFileToStorage(
        formData,
        token.value!,
        (progress) => console.log("Upload progress:", progress),
      );

      const proxyUrl = getStorageProxyUrl(imageUrl);

      const range = editorInstance.getSelection();
      editorInstance.insertEmbed(range?.index || 0, "image", proxyUrl);
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Failed to upload image. Please try again.");
    }
  };
};

watch(
  () => props.content,
  (newContent, oldContent) => {
    if (newContent !== oldContent) {
      content.value = newContent;
      if (editorInstance) {
        editorInstance.clipboard.dangerouslyPasteHTML(newContent);
      }
    }
  },
);

onMounted(() => {
  if (!editor.value) {
    return;
  }

  editorInstance = new Quill(editor.value, {
    theme: "snow",
    modules: {
      toolbar: {
        container: [
          ["bold", "italic", "underline"],
          ["link", "image"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      imageResize: {},
    },
  });

  editorInstance.clipboard.dangerouslyPasteHTML(props.content);
  editorInstance.on("text-change", () => {
    content.value = editorInstance.root.innerHTML;
  });
});

onBeforeUnmount(() => {
  (
    editorInstance?.getModule("imageResize") as ImageResize | undefined
  )?.destroy();
});

const hasChanges = computed(() => content.value !== props.content);
watch(hasChanges, () => {
  emits("changeState", hasChanges.value);
});

const submit = async () => {
  emits("submit", content.value);
};
</script>

<template>
  <div class="toolbar_body">
    <div class="row">
      <div ref="editor" :style="{ height: editorHeight }"></div>
    </div>
  </div>
  <div v-if="showSaveButton" class="toolbar_footer" @click="submit">
    <div class="button primary" :class="{ disabled: !hasChanges }">
      {{ $t("templateEditor.toolbar.save") }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.toolbar_body {
  padding: 1em;
  font-size: 0.9rem;
  background: var(--classic-surface, #fff);
  height: calc(100vh - 60px - 60px - 82px);
  box-sizing: border-box;
  overflow-y: scroll;
}

.toolbar_footer {
  padding: 1em;
  font-size: 0.9rem;
  background: var(--classic-surface, #fff);
  border-top: 1px solid var(--classic-border-subtle, #e0e0e0);
}

.material-symbols-outlined {
  font-size: 1.1rem;
  cursor: pointer;
}

.label_container {
  display: flex;
  margin-bottom: 0.5em;
  align-items: center;
}

.label_container label {
  flex: 1;
  font-weight: 500;
}

.label_container .link {
  font-weight: normal;
}

.row {
  margin-bottom: 1.5em;
}

.image {
  background: var(--classic-border-subtle, #e0e0e0);
  height: 70px;
}

.image img {
  max-width: 100%;
  max-height: 100%;
}

input[type="checkbox"] {
  margin-right: 0.5em;
}
// The editable customer HTML stays on its original light canvas. Only Quill
// chrome (including its floating link editor) belongs to the editor theme.
:deep(.ql-editor) {
  color-scheme: light;
  background: #fff;
  color: #4a4a4a;
}
:deep(.ql-toolbar),
:deep(.ql-container),
:deep(.ql-tooltip) {
  border-color: var(--classic-border, #ccc);
}
:deep(.ql-toolbar),
:deep(.ql-tooltip) {
  background: var(--classic-surface, #fff);
  color: var(--classic-text, #444);
}
:deep(.ql-toolbar .ql-stroke) {
  stroke: var(--classic-text-secondary, #444);
}
:deep(.ql-toolbar .ql-fill) {
  fill: var(--classic-text-secondary, #444);
}
:deep(.ql-toolbar button:hover .ql-stroke),
:deep(.ql-toolbar button.ql-active .ql-stroke) {
  stroke: var(--classic-accent, #06c);
}
:deep(.ql-tooltip input[type="text"]) {
  color: var(--classic-text, CanvasText);
  background: var(--classic-surface, #fff);
  border-color: var(--classic-border, #ccc);
}
:deep(.ql-tooltip a) {
  color: var(--classic-accent, #06c);
}
</style>
